import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// ---------- Client: list own bookings ----------
export const getMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("consultation_bookings")
      .select(
        "id, full_name, service, consultation_type, preferred_date, preferred_time, message, status, payment_status, amount_zar, payment_updated_at, paid_at, created_at",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

// ---------- Client: dashboard summary ----------
export const getMyDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const [bookingsRes, profileRes] = await Promise.all([
      sb
        .from("consultation_bookings")
        .select("id, service, preferred_date, preferred_time, status, payment_status, amount_zar, payment_updated_at, paid_at, created_at, consultation_type")
        .eq("user_id", context.userId)
        .order("created_at", { ascending: false })
        .limit(50),
      sb
        .from("profiles")
        .select("email")
        .eq("id", context.userId)
        .maybeSingle(),
    ]);

    const email = profileRes.data?.email ?? "";
    const enquiriesRes = email
      ? await sb
          .from("contact_messages")
          .select("id, service, message, created_at")
          .eq("email", email)
          .order("created_at", { ascending: false })
          .limit(50)
      : { data: [] as any[] };

    const docsRes = await sb
      .from("client_documents")
      .select("id, original_filename, case_reference, case_status, payment_status, created_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);

    const bookings = bookingsRes.data ?? [];
    const enquiries = (enquiriesRes.data as any[]) ?? [];
    const documents = docsRes.data ?? [];

    const now = Date.now();
    const upcoming = bookings.filter((b: any) => {
      const t = new Date(`${b.preferred_date}T${b.preferred_time || "09:00"}:00`).getTime();
      return t >= now && b.status !== "cancelled";
    });

    const counts = {
      bookingsTotal: bookings.length,
      bookingsUpcoming: upcoming.length,
      enquiriesTotal: enquiries.length,
      documentsTotal: documents.length,
      paymentsPending: bookings.filter((b: any) => b.status === "pending").length,
      paymentsConfirmed: bookings.filter((b: any) => b.status === "confirmed" || b.status === "completed").length,
      docsPaid: documents.filter((d: any) => d.payment_status === "paid").length,
      docsUnpaid: documents.filter((d: any) => d.payment_status && d.payment_status !== "paid").length,
    };

    return {
      counts,
      upcoming: upcoming.slice(0, 5),
      recentBookings: bookings.slice(0, 5),
      recentEnquiries: enquiries.slice(0, 5),
      recentDocuments: documents.slice(0, 5),
    };
  });

// ---------- Profile ----------
export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: profile } = await context.supabase
      .from("profiles")
      .select("id, email, full_name, phone")
      .eq("id", context.userId)
      .maybeSingle();

    const { data: isAdminRpc } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });

    return {
      profile: profile ?? { id: context.userId, email: "", full_name: "", phone: "" },
      isAdmin: Boolean(isAdminRpc),
    };
  });

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        full_name: z.string().trim().min(1).max(120),
        phone: z.string().trim().max(40).optional().default(""),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({ full_name: data.full_name, phone: data.phone })
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Admin ----------
async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

export const adminListBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("consultation_bookings")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

export const adminListEnquiries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("contact_messages")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { messages: data ?? [] };
  });

export const adminUpdateBookingStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["pending", "confirmed", "completed", "cancelled"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin
      .from("consultation_bookings")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- SMS diagnostic (admin-only) ----------
export const adminTestSms = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ to: z.string().min(6).max(40) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { sendSms } = await import("@/lib/sms.server");
    const r = await sendSms(
      data.to,
      "Test SMS from Nkadimeng Danny portal — please ignore.",
    );
    return {
      ok: r.ok,
      to: r.to,
      apiMessageId: r.apiMessageId ?? null,
      error: r.error ?? null,
      raw: r.raw ? JSON.stringify(r.raw) : null,
    };
  });

// ---------- Client: resend my booking receipt ----------
export const resendMyBookingReceipt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ id: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { data: b, error } = await context.supabase
      .from("consultation_bookings")
      .select(
        "id, full_name, email, phone, service, consultation_type, preferred_date, preferred_time, message, payment_status, amount_zar",
      )
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error || !b) throw new Error("Booking not found");

    const { sendSystemEmail } = await import("@/lib/email/system-send.server");
    const SITE = "https://nkadimengdannylcs.com";
    const invoiceNumber = `INV-${b.id.slice(0, 6).toUpperCase()}`;
    await sendSystemEmail({
      templateName: "booking-confirmed",
      recipientEmail: b.email,
      templateData: {
        audience: "client",
        fullName: b.full_name,
        email: b.email,
        phone: b.phone,
        service: b.service,
        consultationType: b.consultation_type,
        preferredDate: b.preferred_date,
        preferredTime: b.preferred_time,
        invoiceNumber,
        amountZar: b.amount_zar ? `R ${Number(b.amount_zar).toFixed(2)}` : "",
        paymentStatus: b.payment_status ?? "unpaid",
        invoiceUrl: `${SITE}/invoice/${b.id}`,
        icsUrl: `${SITE}/api/public/booking-ics/${b.id}`,
        rescheduleUrl: `${SITE}/book?service=${encodeURIComponent(b.service)}`,
      },
      // unique key so each manual resend goes out fresh
      idempotencyKey: `booking-resend-${b.id}-${Date.now()}`,
    });
    return { ok: true };
  });

// ---------- Admin: update payment status on a booking ----------
export const adminUpdateBookingPayment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        payment_status: z.enum(["unpaid", "pending", "paid", "refunded", "failed"]),
        amount_zar: z.number().nonnegative().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const patch: Record<string, any> = {
      payment_status: data.payment_status,
      payment_updated_at: new Date().toISOString(),
    };
    if (typeof data.amount_zar === "number") patch.amount_zar = data.amount_zar;
    if (data.payment_status === "paid") {
      patch.paid_at = new Date().toISOString();
      patch.status = "confirmed";
    }
    const { error } = await supabaseAdmin
      .from("consultation_bookings")
      .update(patch as any)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });



// ---------- Admin: update payment status on a client document ----------
export const adminUpdateDocumentPayment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        payment_status: z.enum(["unpaid", "partial", "paid", "waived"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin
      .from("client_documents")
      .update({ payment_status: data.payment_status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Admin: list payment events (audit/diagnostics) ----------
export const adminListPaymentEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("payment_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { events: data ?? [] };
  });

// ---------- Client: list my payments (credit + contract orders) ----------
export const listMyPayments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const { data: profile } = await sb
      .from("profiles")
      .select("email")
      .eq("id", context.userId)
      .maybeSingle();
    const email = (profile?.email ?? "").toLowerCase();

    const [creditsRes, contractsByUser, contractsByEmail, cvRes, subRes, llbRes, manualRes, instRes] =
      await Promise.all([
        sb.from("credit_orders")
          .select("reference, pack_code, credits, amount_zar, status, created_at, paid_at, receipt_sent_at, paid_provider_ref, public_token")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false })
          .limit(100),
        email
          ? sb.from("contract_orders")
              .select("reference, slug, title, client_name, client_email, amount_zar, status, created_at, paid_at, receipt_sent_at, paid_provider_ref, download_token")
              .eq("client_email", email)
              .order("created_at", { ascending: false })
              .limit(100)
          : Promise.resolve({ data: [] as any[] }),
        Promise.resolve({ data: [] as any[] }),
        sb.from("cv_orders" as any)
          .select("reference, quantity, amount_zar, status, created_at, paid_at, receipt_sent_at, paid_provider_ref, public_token")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false })
          .limit(100),

        sb.from("subscription_orders" as any)
          .select("reference, plan_code, amount_zar, status, created_at, paid_at, receipt_sent_at, paid_provider_ref")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false })
          .limit(100),
        sb.from("llb_tutoring_orders" as any)
          .select("reference, modules_count, amount_zar, status, created_at, paid_at, receipt_sent_at, paid_provider_ref, student_email")
          .eq("user_id", context.userId)
          .order("created_at", { ascending: false })
          .limit(100)
          .then((r: any) => r, () => ({ data: [] as any[] })),
        email
          ? sb.from("manual_invoices" as any)
              .select("reference, client_name, client_email, total_cents, status, created_at, paid_at, paid_provider_ref")
              .eq("client_email", email)
              .order("created_at", { ascending: false })
              .limit(100)
              .then((r: any) => r, () => ({ data: [] as any[] }))
          : Promise.resolve({ data: [] as any[] }),
        email
          ? sb.from("brand_installment_payments" as any)
              .select("amount_cents, method, paid_at, note, plan_id, brand_installment_plans:plan_id (reference, recipient_email, recipient_name)")
              .order("paid_at", { ascending: false })
              .limit(100)
              .then((r: any) => ({
                data: ((r?.data ?? []) as any[]).filter(
                  (row: any) => (row?.brand_installment_plans?.recipient_email ?? "").toLowerCase() === email,
                ),
              }), () => ({ data: [] as any[] }))
          : Promise.resolve({ data: [] as any[] }),
      ]);

    // Strip any renewal suffix (e.g. "SUB-abc-R173..." → "SUB-abc") so the
    // "Original invoice / reference" column always shows the parent
    // subscription/order the customer first paid on.
    const originalRef = (ref: string) => String(ref || "").replace(/-R\d{6,}$/i, "");

    const credits = (creditsRes.data ?? []).map((c: any) => ({
      kind: "credit" as const,
      reference: c.reference,
      invoice_reference: originalRef(c.reference),
      title: `${c.credits} AI credits (${c.pack_code})`,
      amount_zar: Number(c.amount_zar),
      status: c.status,
      created_at: c.created_at,
      paid_at: c.paid_at,
      receipt_sent_at: c.receipt_sent_at,
      pf_payment_id: c.paid_provider_ref,
      public_token: c.public_token ?? null,
    }));

    const contractMap = new Map<string, any>();
    for (const c of [...(contractsByUser.data ?? []), ...(contractsByEmail.data ?? [])]) {
      if (!contractMap.has(c.reference)) contractMap.set(c.reference, c);
    }
    const contracts = Array.from(contractMap.values()).map((c: any) => ({
      kind: "contract" as const,
      reference: c.reference,
      invoice_reference: originalRef(c.reference),
      title: c.title,
      amount_zar: Number(c.amount_zar),
      status: c.status,
      created_at: c.created_at,
      paid_at: c.paid_at,
      receipt_sent_at: c.receipt_sent_at,
      pf_payment_id: c.paid_provider_ref,
      download_token: c.download_token ?? null,
    }));
    const cvs = (cvRes.data ?? []).map((c: any) => ({
      kind: "cv" as const,
      reference: c.reference,
      invoice_reference: originalRef(c.reference),
      title: `${c.quantity} × AI CV generation`,
      amount_zar: Number(c.amount_zar),
      status: c.status,
      created_at: c.created_at,
      paid_at: c.paid_at,
      receipt_sent_at: c.receipt_sent_at,
      pf_payment_id: c.paid_provider_ref,
      public_token: c.public_token ?? null,
    }));

    const subs = ((subRes as any).data ?? []).map((s: any) => ({
      kind: "subscription" as const,
      reference: s.reference,
      invoice_reference: originalRef(s.reference),
      title: `Subscription · ${String(s.plan_code || "plan").toUpperCase()}`,
      amount_zar: Number(s.amount_zar),
      status: s.status,
      created_at: s.created_at,
      paid_at: s.paid_at,
      receipt_sent_at: s.receipt_sent_at,
      pf_payment_id: s.paid_provider_ref,
    }));
    const llb = (((llbRes as any)?.data ?? []) as any[]).map((r: any) => ({
      kind: "llb_tutoring" as const,
      reference: r.reference,
      invoice_reference: originalRef(r.reference),
      title: `LLB tutoring · ${r.modules_count ?? 1} module(s)`,
      amount_zar: Number(r.amount_zar),
      status: r.status,
      created_at: r.created_at,
      paid_at: r.paid_at,
      receipt_sent_at: r.receipt_sent_at,
      pf_payment_id: r.paid_provider_ref,
    }));
    const manual = (((manualRes as any)?.data ?? []) as any[]).map((r: any) => ({
      kind: "invoice" as const,
      reference: r.reference,
      invoice_reference: r.reference,
      title: `Invoice ${r.reference}`,
      amount_zar: Number(r.total_cents ?? 0) / 100,
      status: r.status,
      created_at: r.created_at,
      paid_at: r.paid_at,
      receipt_sent_at: null,
      pf_payment_id: r.paid_provider_ref,
    }));
    const installments = (((instRes as any)?.data ?? []) as any[]).map((r: any) => ({
      kind: "installment" as const,
      reference: r.brand_installment_plans?.reference ?? r.plan_id,
      // The installment payment is always tied to the parent brand-plan
      // reference the client received on their original invoice.
      invoice_reference: r.brand_installment_plans?.reference ?? r.plan_id,
      title: `Installment payment · ${r.brand_installment_plans?.recipient_name ?? "plan"}`,
      amount_zar: Number(r.amount_cents ?? 0) / 100,
      status: "paid",
      created_at: r.paid_at,
      paid_at: r.paid_at,
      receipt_sent_at: null,
      pf_payment_id: null,
    }));

    const all = [...credits, ...contracts, ...cvs, ...subs, ...llb, ...manual, ...installments].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return { payments: all };
  });


// ---------- Client: resend my receipt (paid orders only) ----------
export const resendMyPaymentReceipt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ reference: z.string().trim().min(4).max(80) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: profile } = await sb
      .from("profiles")
      .select("email, full_name")
      .eq("id", context.userId)
      .maybeSingle();
    const email = (profile?.email ?? "").toLowerCase();

    // Verify ownership via user_id or matching email
    const { data: credit } = await sb
      .from("credit_orders")
      .select("reference, user_id")
      .eq("reference", data.reference)
      .maybeSingle();
    let owned = !!(credit && credit.user_id === context.userId);
    if (!credit) {
      const { data: contract } = await sb
        .from("contract_orders")
        .select("reference, client_email")
        .eq("reference", data.reference)
        .maybeSingle();
      owned = !!(
        contract && (contract.client_email ?? "").toLowerCase() === email
      );
      if (!owned) {
        const { data: cvOrder } = await sb
          .from("cv_orders" as any)
          .select("reference, user_id")
          .eq("reference", data.reference)
          .maybeSingle();
        owned = !!(cvOrder && (cvOrder as any).user_id === context.userId);
      }
    }
    if (!owned) throw new Error("Not authorized for this order");

    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { sendPayfastReceiptEmail } = await import(
      "@/lib/payfast-receipt.server"
    );
    const origin =
      process.env.PUBLIC_SITE_URL || "https://nkadimengdannylcs.com";

    // Re-derive order context (admin client to avoid RLS surprises after ownership check)
    const { data: c2 } = await supabaseAdmin
      .from("credit_orders")
      .select("reference, pack_code, credits, amount_zar, status, paid_at, paid_provider_ref, receipt_email_to")
      .eq("reference", data.reference)
      .maybeSingle();
    let kind: "credit" | "contract" | "cv" = "credit";
    let itemTitle = "";
    let amountZar = 0;
    let paidAt: string | Date | null = null;
    let pfPaymentId: string | null = null;
    let buyerEmail = "";
    if (c2) {
      if (c2.status !== "paid") throw new Error("Order is not paid yet.");
      itemTitle = `${c2.credits} AI credits (${c2.pack_code})`;
      amountZar = Number(c2.amount_zar);
      paidAt = c2.paid_at;
      pfPaymentId = c2.paid_provider_ref;
      buyerEmail = c2.receipt_email_to || email;
    } else {
      const { data: k2 } = await supabaseAdmin
        .from("contract_orders")
        .select("reference, title, client_name, client_email, amount_zar, status, paid_at, paid_provider_ref")
        .eq("reference", data.reference)
        .maybeSingle();
      if (k2) {
        if (k2.status !== "paid") throw new Error("Order is not paid yet.");
        kind = "contract";
        itemTitle = k2.title;
        amountZar = Number(k2.amount_zar);
        paidAt = k2.paid_at;
        pfPaymentId = k2.paid_provider_ref;
        buyerEmail = k2.client_email || email;
      } else {
        const { data: cv2 } = await supabaseAdmin
          .from("cv_orders" as any)
          .select("reference, quantity, amount_zar, status, paid_at, paid_provider_ref, receipt_email_to")
          .eq("reference", data.reference)
          .maybeSingle();
        if (!cv2) throw new Error("Order not found");
        const cvo: any = cv2;
        if (cvo.status !== "paid") throw new Error("Order is not paid yet.");
        kind = "cv";
        itemTitle = `${cvo.quantity} × AI CV generation`;
        amountZar = Number(cvo.amount_zar);
        paidAt = cvo.paid_at;
        pfPaymentId = cvo.paid_provider_ref;
        buyerEmail = cvo.receipt_email_to || email;
      }
    }
    if (!buyerEmail) throw new Error("No email on file");

    // Rate-limit: at most one resend per minute per reference to prevent
    // duplicate receipt records.
    const since = new Date(Date.now() - 60_000).toISOString();
    const { data: recent } = await supabaseAdmin
      .from("email_send_log")
      .select("id")
      .eq("template_name", "payfast-receipt")
      .eq("recipient_email", buyerEmail.toLowerCase())
      .gte("created_at", since)
      .limit(1);
    if (recent && recent.length > 0) {
      return {
        ok: false,
        rateLimited: true,
        message: "A receipt was already sent in the last minute. Please check your inbox.",
        email: buyerEmail,
      };
    }

    // Treat CV receipts as the "credit" template kind (same email template used by ITN handler).
    const receiptKind = kind === "cv" ? "credit" : kind;
    const sent = await sendPayfastReceiptEmail({
      kind: receiptKind,
      reference: data.reference,
      buyerEmail,
      buyerName: profile?.full_name ?? null,
      itemTitle,
      amountZar,
      paidAt: paidAt ?? new Date(),
      pfPaymentId,
      origin,
    });
    if (sent.ok) {
      const table =
        kind === "credit"
          ? "credit_orders"
          : kind === "contract"
            ? "contract_orders"
            : "cv_orders";
      await supabaseAdmin
        .from(table as any)
        .update({ receipt_sent_at: new Date().toISOString() })
        .eq("reference", data.reference);
      return { ok: true, email: buyerEmail };
    }
    // queue retry on failure (idempotent via unique index)
    const { enqueueReceiptJob } = await import("@/lib/receipt-queue.server");
    await enqueueReceiptJob({
      reference: data.reference,
      kind: receiptKind,
      buyerEmail,
      reason: sent.reason ?? "send_failed",
      triggeredBy: `client:${context.userId}`,
    });
    return { ok: false, queued: true, reason: sent.reason ?? "send_failed" };
  });

// ---------- Client: latest invoice/receipt delivery status per reference ----------
export const listMyPaymentDeliveryStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ references: z.array(z.string().min(4).max(80)).max(100) }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    if (data.references.length === 0) return { statuses: {} };
    const { data: rows } = await supabaseAdmin
      .from("email_send_log")
      .select("template_name, recipient_email, status, created_at, metadata, error_message")
      .in("template_name", ["invoice-created", "payfast-receipt"])
      .filter("metadata->>reference", "in", `(${data.references.map((r) => `"${r}"`).join(",")})`)
      .order("created_at", { ascending: false })
      .limit(500);

    // Pending receipt_jobs for retry counts
    const { data: jobs } = await supabaseAdmin
      .from("receipt_jobs")
      .select("reference, status, attempts, last_error")
      .in("reference", data.references)
      .in("status", ["pending", "failed"])
      .limit(500);

    const statuses: Record<string, {
      invoice?: { status: string; at: string; error?: string | null };
      receipt?: { status: string; at: string; error?: string | null; retries?: number };
    }> = {};
    for (const r of rows ?? []) {
      const ref = (r.metadata as any)?.reference;
      if (!ref) continue;
      const slot = (statuses[ref] ??= {});
      const docKey = r.template_name === "invoice-created" ? "invoice" : "receipt";
      if (!slot[docKey]) {
        slot[docKey] = {
          status: r.status,
          at: r.created_at,
          error: r.error_message,
        };
      }
    }
    for (const j of jobs ?? []) {
      const slot = (statuses[j.reference] ??= {});
      slot.receipt = {
        status: j.status === "pending" ? "retrying" : "failed",
        at: new Date().toISOString(),
        error: j.last_error,
        retries: j.attempts ?? 0,
      };
    }
    return { statuses };
  });

// ---------- Client: full detail (events, emails, jobs) for one of my orders ----------
export const getMyPaymentDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ reference: z.string().trim().min(4).max(80) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: profile } = await sb
      .from("profiles")
      .select("email")
      .eq("id", context.userId)
      .maybeSingle();
    const email = (profile?.email ?? "").toLowerCase();

    // ownership check (same logic as resend)
    const { data: credit } = await sb
      .from("credit_orders")
      .select("reference, user_id")
      .eq("reference", data.reference)
      .maybeSingle();
    let owned = !!(credit && credit.user_id === context.userId);
    if (!credit) {
      const { data: contract } = await sb
        .from("contract_orders")
        .select("reference, client_email")
        .eq("reference", data.reference)
        .maybeSingle();
      owned = !!(
        contract && (contract.client_email ?? "").toLowerCase() === email
      );
      if (!owned) {
        const { data: cvOrder } = await sb
          .from("cv_orders" as any)
          .select("reference, user_id")
          .eq("reference", data.reference)
          .maybeSingle();
        owned = !!(cvOrder && (cvOrder as any).user_id === context.userId);
      }
    }
    if (!owned) throw new Error("Not authorized for this order");

    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const [eventsRes, emailsRes, jobsRes] = await Promise.all([
      supabaseAdmin
        .from("payment_events")
        .select("id, event_type, processed, processed_at, error_message, created_at")
        .eq("reference", data.reference)
        .order("created_at", { ascending: false })
        .limit(50),
      supabaseAdmin
        .from("email_send_log")
        .select("template_name, recipient_email, status, error_message, created_at")
        .in("template_name", [
          "invoice-created",
          "payfast-receipt",
          "payfast-payment-status",
        ])
        .filter("metadata->>reference", "eq", data.reference)
        .order("created_at", { ascending: false })
        .limit(50),
      supabaseAdmin
        .from("receipt_jobs")
        .select("id, status, attempts, last_error, next_retry_at, created_at, updated_at")
        .eq("reference", data.reference)
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    return {
      events: eventsRes.data ?? [],
      emails: emailsRes.data ?? [],
      jobs: jobsRes.data ?? [],
    };
  });

